import React, { useState } from 'react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  Sparkles, 
  ExternalLink, 
  Check,
  ArrowUp
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 4000);
  };

  return (
    <div className="bg-[#fafaf7] text-[#0b3a24] font-sans min-h-screen relative overflow-hidden">
      
      {/* Hero Header Banner (Dark Kerala Emerald Green & Gold) */}
      <section className="relative bg-gradient-to-br from-[#041a0f] via-[#09100d] to-[#020906] text-white pt-36 pb-24 px-4 sm:px-6 lg:px-8 border-b border-[#eddca6]/20 overflow-hidden">
        
        {/* Ambient Light Glow Blobs */}
        <div className="absolute top-1/3 left-1/3 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-gold-900/20 to-transparent rounded-full filter blur-[140px] pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-[#eddca6]/10 rounded-full filter blur-[100px] pointer-events-none"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 bg-[#eddca6]/10 border border-[#eddca6]/30 text-[#eddca6] px-4.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-sm">
            <MapPin className="w-3.5 h-3.5 text-[#eddca6]" />
            LOCATION & ASSISTANCE
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black tracking-widest uppercase leading-tight text-white drop-shadow-md">
            CONTACT <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#eddca6] via-[#f5edd2] to-[#c5a85a]">CHIRAYIL</span>
          </h1>

          <p className="max-w-2xl mx-auto text-xs sm:text-sm text-white/80 font-light leading-relaxed">
            Have questions about our custom jewellery designs, daily gold rates, or savings schemes? Visit our flagship showroom in Pulpally or send us a message.
          </p>

          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#eddca6] to-transparent mx-auto pt-2"></div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#fafaf7]">
        <div className="max-w-7xl mx-auto space-y-16">
          
          {/* Contact Info Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Address Card */}
            <div className="bg-white rounded-3xl p-7 border border-gold-300 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#0b3a24] rounded-2xl flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-300">
                  <MapPin className="w-5 h-5 text-[#eddca6]" />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0b3a24] mb-1">Our Showroom</h3>
                  <p className="text-xs text-[#0b3a24]/75 font-light leading-relaxed">
                    PERIKALLUR Road, Pulpally, Wayanad, Kerala - 673579
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gold-100 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gold-600">
                <span>Pulpally Branch</span>
                <span>Flagship</span>
              </div>
            </div>

            {/* Call Us Card */}
            <div className="bg-white rounded-3xl p-7 border border-gold-300 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#0b3a24] rounded-2xl flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Phone className="w-5 h-5 text-[#eddca6]" />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0b3a24] mb-1">Phone Enquiries</h3>
                  <div className="space-y-1">
                    <a href="tel:+919605155088" className="block text-xs font-semibold text-[#0b3a24] hover:text-[#c5a85a] transition-colors">
                      +91 9605155088
                    </a>
                    <a href="tel:+916282370250" className="block text-xs font-semibold text-[#0b3a24] hover:text-[#c5a85a] transition-colors">
                      +91 6282370250
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gold-100 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gold-600">
                <span>Call Directly</span>
                <span>Mon-Sun</span>
              </div>
            </div>

            {/* Email Us Card */}
            <div className="bg-white rounded-3xl p-7 border border-gold-300 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#0b3a24] rounded-2xl flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Mail className="w-5 h-5 text-[#eddca6]" />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0b3a24] mb-1">Email Support</h3>
                  <a 
                    href="mailto:info@chirayilgoldanddiamonds.com" 
                    className="text-xs font-semibold text-[#0b3a24] hover:text-[#c5a85a] transition-colors block truncate"
                  >
                    info@chirayilgoldanddiamonds.com
                  </a>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gold-100 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gold-600">
                <span>Official Mail</span>
                <span>24/7 Support</span>
              </div>
            </div>

            {/* Opening Hours Card */}
            <div className="bg-white rounded-3xl p-7 border border-gold-300 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-300 group flex flex-col justify-between">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-[#0b3a24] rounded-2xl flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-300">
                  <Clock className="w-5 h-5 text-[#eddca6]" />
                </div>
                <div>
                  <h3 className="text-lg font-serif font-bold text-[#0b3a24] mb-1">Showroom Hours</h3>
                  <p className="text-xs text-[#0b3a24]/75 font-light leading-relaxed">
                    Monday - Sunday: 10:00 AM - 08:00 PM
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gold-100 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-emerald-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Open Daily
                </span>
                <span>7 Days</span>
              </div>
            </div>

          </div>

          {/* Form & Map Split Grid */}
          <div className="grid lg:grid-cols-12 gap-12 items-stretch">
            
            {/* Left Column: Send Inquiry Form */}
            <div className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-12 border border-gold-300 shadow-xl flex flex-col justify-between">
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#0b3a24] bg-[#fafaf7] border border-gold-300 px-3.5 py-1 rounded-full">
                    <Sparkles className="w-3.5 h-3.5 text-[#0b3a24]" />
                    QUICK INQUIRY
                  </div>
                  <h2 className="text-3xl font-serif font-black text-[#0b3a24]">
                    Send Us a Message
                  </h2>
                  <p className="text-xs text-[#0b3a24]/75 font-light">
                    Fill out the form below and our team will get back to you promptly.
                  </p>
                </div>

                {submitted ? (
                  <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 p-6 rounded-2xl text-center space-y-2 animate-fade-in">
                    <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center mx-auto">
                      <Check className="w-6 h-6" />
                    </div>
                    <h4 className="font-bold text-sm">Thank You for Reaching Out!</h4>
                    <p className="text-xs text-emerald-700 font-light">
                      Your inquiry has been received. Our representative will contact you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-[#0b3a24]">Your Name</label>
                        <input
                          type="text"
                          name="name"
                          placeholder="e.g. Anjali Menon"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3.5 bg-[#fafaf7] border-b-2 border-gold-300 focus:border-[#0b3a24] focus:bg-white outline-none text-xs font-semibold text-[#0b3a24] transition-all"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[10px] font-bold uppercase tracking-wider text-[#0b3a24]">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          placeholder="e.g. anjali@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3.5 bg-[#fafaf7] border-b-2 border-gold-300 focus:border-[#0b3a24] focus:bg-white outline-none text-xs font-semibold text-[#0b3a24] transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#0b3a24]">Subject of Interest</label>
                      <input
                        type="text"
                        name="subject"
                        placeholder="e.g. Gold Scheme Enquiry / Bridal Lounge Appointment"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3.5 bg-[#fafaf7] border-b-2 border-gold-300 focus:border-[#0b3a24] focus:bg-white outline-none text-xs font-semibold text-[#0b3a24] transition-all"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold uppercase tracking-wider text-[#0b3a24]">Your Message</label>
                      <textarea
                        name="message"
                        placeholder="Describe how we can assist you..."
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3.5 bg-[#fafaf7] border-b-2 border-gold-300 focus:border-[#0b3a24] focus:bg-white outline-none text-xs font-medium text-[#0b3a24] transition-all resize-none"
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        className="gold-shine-btn bg-gradient-to-r from-[#0b3a24] via-[#0e482d] to-[#0b3a24] text-white font-bold px-10 py-4 rounded-xl text-xs uppercase tracking-widest hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2 w-full sm:w-auto font-sans"
                      >
                        <span>Send Message</span>
                        <Send className="w-4 h-4 text-[#eddca6]" />
                      </button>
                    </div>
                  </form>
                )}

              </div>
            </div>

            {/* Right Column: Google Maps View */}
            <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
              
              {/* Map Container */}
              <div className="h-full min-h-[380px] bg-white border-2 border-[#eddca6] rounded-3xl p-4 shadow-xl flex flex-col justify-between relative overflow-hidden group">
                <div className="w-full h-full min-h-[340px] rounded-2xl overflow-hidden relative border border-gold-300 bg-neutral-100 shadow-inner">
                  <iframe
                    title="Chirayil Gold and Diamonds Google Map View"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3904.7019517216722!2d76.1503030750581!3d11.85612398836603!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1734508758906!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full object-cover filter contrast-[1.05]"
                  ></iframe>

                  {/* Overlapping Badge */}
                  <div className="absolute top-4 left-4 right-4 bg-white/90 backdrop-blur-md p-3.5 rounded-xl border border-gold-300 shadow-lg flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
                      <span className="text-xs font-bold text-[#0b3a24]">Chirayil Pulpally Showroom</span>
                    </div>
                    <span className="text-[9px] font-bold text-[#0b3a24]/70 bg-gold-200/50 px-2.5 py-1 rounded-md uppercase tracking-wider font-sans">Wayanad</span>
                  </div>
                </div>
              </div>

              {/* Direct Directions CTA */}
              <a 
                href="https://www.google.com/maps/place/Chirayil+Gold+%26+Diamonds/@11.856124,76.150303,16z"
                target="_blank"
                rel="noopener noreferrer"
                className="gold-shine-btn bg-gradient-to-r from-[#0b3a24] via-[#0e482d] to-[#0b3a24] text-white font-bold px-8 py-4 rounded-xl text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 font-sans hover:scale-[1.01] transition-all"
              >
                <span>Get Directions on Google Maps</span>
                <ExternalLink className="w-4 h-4 text-[#eddca6]" />
              </a>

            </div>

          </div>

        </div>
      </section>

      {/* Floating Scroll to Top button */}
      <div className="fixed bottom-8 right-8 z-50">
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="w-12 h-12 bg-gradient-to-br from-[#0b3a24] to-[#0e482d] text-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 active:scale-90 transition-all duration-300 group border border-gold-300/40"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 transform group-hover:-translate-y-0.5 transition-transform duration-300" />
        </button>
      </div>

    </div>
  );
};

export default Contact;